import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  VStack,
  HStack,
  Badge,
  Icon,
  Spinner,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  Tooltip,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
} from '@chakra-ui/react';
import {
  FaSearch,
  FaDollarSign,
  FaChartLine,
  FaInfoCircle,
  FaStar,
  FaArrowUp,
  FaArrowDown,
  FaBuilding,
} from 'react-icons/fa';
import AnimatedBox from '../AnimatedBox';
import { SectionLabel } from './DashboardWrapper';
import { getStoredRevenueEstimates } from '../../services/revenueService';
import { logger } from '../../utils/logger';

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const getConfidenceColor = (score) => {
  if (score >= 0.7) return 'green';
  if (score >= 0.4) return 'yellow';
  return 'red';
};

const getConfidenceLabel = (score) => {
  if (score >= 0.7) return 'High';
  if (score >= 0.4) return 'Medium';
  return 'Low';
};

const RevenueEstimationDashboard = ({ reviewsData, dateRange, sectionLabel }) => {
  const [revenueData, setRevenueData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtextColor = useColorModeValue('gray.500', 'gray.400');
  const searchBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    const fetchRevenueData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getStoredRevenueEstimates();
        setRevenueData(data);
      } catch (err) {
        logger.error('Error fetching revenue estimates:', err);
        setError(err.message || 'Failed to load revenue estimates');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRevenueData();
  }, []);

  const filteredEstimates = useMemo(() => {
    if (!revenueData?.revenue_estimates) return [];
    if (!searchTerm) return revenueData.revenue_estimates;
    const term = searchTerm.toLowerCase();
    return revenueData.revenue_estimates.filter(
      (est) => (est.business_name || est.name || '').toLowerCase().includes(term)
    );
  }, [revenueData, searchTerm]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color={subtextColor}>Loading revenue estimates...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <VStack spacing={4}>
          <Icon as={FaInfoCircle} boxSize={10} color="red.400" />
          <Text color="red.500" fontSize="lg">{error}</Text>
          <Text color={subtextColor} fontSize="sm">
            Revenue estimates may not be available for this vertical yet.
          </Text>
        </VStack>
      </Flex>
    );
  }

  if (!revenueData || revenueData.total_businesses === 0) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <VStack spacing={4}>
          <Icon as={FaDollarSign} boxSize={10} color="gray.400" />
          <Text color={subtextColor} fontSize="lg">No revenue estimates available</Text>
          <Text color={subtextColor} fontSize="sm">
            Revenue estimates have not been generated for this vertical yet.
          </Text>
        </VStack>
      </Flex>
    );
  }

  const portfolio = revenueData.portfolio_summary || {};
  const monthlyRevenue = portfolio.total_monthly_revenue || {};
  const annualRevenue = portfolio.total_annual_revenue || {};

  return (
    <Box>
      <SectionLabel description="AI-powered revenue estimates based on reviews, traffic, and competitive data.">
        {sectionLabel || 'Revenue Estimation'}
      </SectionLabel>

      {/* Portfolio Summary Cards */}
      <AnimatedBox animation="slideUp" delay={0.1} duration={0.5}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <StatLabel color={subtextColor}>
                  <HStack spacing={2}>
                    <Icon as={FaDollarSign} color="green.500" />
                    <Text>Monthly Revenue (Market)</Text>
                  </HStack>
                </StatLabel>
                <StatNumber fontSize="2xl" color="green.500">
                  {formatCurrency(monthlyRevenue.median)}
                </StatNumber>
                <StatHelpText color={subtextColor}>
                  {formatCurrency(monthlyRevenue.low)} - {formatCurrency(monthlyRevenue.high)}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <StatLabel color={subtextColor}>
                  <HStack spacing={2}>
                    <Icon as={FaChartLine} color="blue.500" />
                    <Text>Annual Revenue (Market)</Text>
                  </HStack>
                </StatLabel>
                <StatNumber fontSize="2xl" color="blue.500">
                  {formatCurrency(annualRevenue.median)}
                </StatNumber>
                <StatHelpText color={subtextColor}>
                  {formatCurrency(annualRevenue.low)} - {formatCurrency(annualRevenue.high)}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <StatLabel color={subtextColor}>
                  <HStack spacing={2}>
                    <Icon as={FaBuilding} color="purple.500" />
                    <Text>Businesses Analyzed</Text>
                  </HStack>
                </StatLabel>
                <StatNumber fontSize="2xl" color="purple.500">
                  {revenueData.total_businesses}
                </StatNumber>
                <StatHelpText color={subtextColor}>
                  Avg confidence: {((portfolio.average_confidence_score || 0) * 100).toFixed(0)}%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      </AnimatedBox>

      {/* Search */}
      <AnimatedBox animation="fadeIn" delay={0.2} duration={0.5} mb={4}>
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <Icon as={FaSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search businesses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg={searchBg}
            borderColor={borderColor}
          />
        </InputGroup>
      </AnimatedBox>

      {/* Business Revenue Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
        {filteredEstimates.map((estimate, index) => {
          const revenueEst = estimate.revenue_estimate || {};
          const confidence = estimate.confidence_score || 0;
          const businessName = estimate.business_name || estimate.name || 'Unknown';
          const methodology = estimate.methodology || '';

          return (
            <AnimatedBox
              key={estimate._id || index}
              animation="slideUp"
              delay={0.05 * Math.min(index, 10)}
              duration={0.4}
            >
              <Card
                bg={cardBg}
                borderColor={borderColor}
                borderWidth="1px"
                _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    {/* Business Name */}
                    <HStack justify="space-between" align="start">
                      <Text fontWeight="bold" fontSize="md" noOfLines={2}>
                        {businessName}
                      </Text>
                      <Tooltip label={`Confidence: ${(confidence * 100).toFixed(0)}%`}>
                        <Badge
                          colorScheme={getConfidenceColor(confidence)}
                          fontSize="xs"
                          flexShrink={0}
                        >
                          {getConfidenceLabel(confidence)}
                        </Badge>
                      </Tooltip>
                    </HStack>

                    <Divider />

                    {/* Monthly Revenue */}
                    <VStack align="stretch" spacing={1}>
                      <Text fontSize="xs" color={subtextColor} fontWeight="medium">
                        Monthly Revenue Estimate
                      </Text>
                      <Text fontSize="xl" fontWeight="bold" color="green.500">
                        {formatCurrency(revenueEst.monthly_median)}
                      </Text>
                      <Text fontSize="xs" color={subtextColor}>
                        Range: {formatCurrency(revenueEst.monthly_low)} - {formatCurrency(revenueEst.monthly_high)}
                      </Text>
                    </VStack>

                    {/* Confidence Bar */}
                    <VStack align="stretch" spacing={1}>
                      <HStack justify="space-between">
                        <Text fontSize="xs" color={subtextColor}>Confidence</Text>
                        <Text fontSize="xs" color={subtextColor}>
                          {(confidence * 100).toFixed(0)}%
                        </Text>
                      </HStack>
                      <Progress
                        value={confidence * 100}
                        size="sm"
                        colorScheme={getConfidenceColor(confidence)}
                        borderRadius="full"
                      />
                    </VStack>

                    {/* Rating info if available */}
                    {estimate.rating_avg && (
                      <HStack spacing={2} fontSize="sm" color={subtextColor}>
                        <Icon as={FaStar} color="yellow.400" boxSize={3} />
                        <Text>{estimate.rating_avg.toFixed(1)}</Text>
                        {estimate.rating_count && (
                          <Text>({estimate.rating_count.toLocaleString()} reviews)</Text>
                        )}
                      </HStack>
                    )}

                    {/* Methodology snippet */}
                    {methodology && (
                      <Text fontSize="xs" color={subtextColor} noOfLines={2}>
                        {methodology}
                      </Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            </AnimatedBox>
          );
        })}
      </SimpleGrid>

      {filteredEstimates.length === 0 && searchTerm && (
        <Flex justify="center" py={8}>
          <Text color={subtextColor}>No businesses match "{searchTerm}"</Text>
        </Flex>
      )}
    </Box>
  );
};

export default RevenueEstimationDashboard;
